import React, { useEffect } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { daContracts } from 'src/constants/daContracts';
import { generateDAcontracts } from 'src/reports/pdf/daContracts';
import { generateOfferLetter } from 'src/reports/pdf/daOfferLetter';
import SignatureBoard from '../Component/SignatureBoard';
import FleetDashboardTabs from '../FleetComponents/FleetDashboardTabs';
import Spinner from '../UI/Spinner';
import { useShowSnackbar } from 'src/hooks/useShowSnackbar';
import PDFViewer from '../UI/PDFViewer';
import Button from '../UI/Button';

/* ─── Reusable PolicyRadioGroup ──────────────────────────────── */
function PolicyRadioGroup({ badge, title, policyText, options, value, onChange }) {
  return (
    <div className="dac-card">
      <div className="dac-card__header">
        <span className="dac-card__badge">{badge}</span>
        <p className="dac-card__title">{title}</p>
      </div>
      {policyText && <p className="dac-card__policy-text">{policyText}</p>}
      <div className="dac-radio-group">
        {options.map((opt) => {
          const checked = value === opt.id;
          return (
            <label
              key={opt.id}
              className={`dac-radio-row${checked ? ' dac-radio-row--checked' : ''}`}
              onClick={() => onChange(opt.id)}
            >
              <div className={`dac-radio-circle${checked ? ' dac-radio-circle--checked' : ''}`}>
                {checked && <div className="dac-radio-dot" />}
              </div>
              <span className={`dac-radio-label${checked ? ' dac-radio-label--checked' : ''}`}>
                {opt.label}
              </span>
              <input
                type="radio"
                name={title}
                value={opt.id}
                checked={checked}
                onChange={() => onChange(opt.id)}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────── */
function DAContractModal(prop) {
  const {
    isOpen,
    toggle,
    setSignature,
    signature,
    selectedDsp,
    daApplyFormData,
    user,
    disable,
    setDAContractJSON,
    daContractJson,
  } = prop;

  if (!isOpen) return null;

  const showSnackbar = useShowSnackbar();
  const [showSignature, setShowSignature] = React.useState(false);
  const [pdfLink, setPdfLink] = React.useState();
  const [activeContract, setActiveContract] = React.useState(daContracts[0]);
  const [activeTab, setActiveTab] = React.useState(0);
  const [pdfLoading, setPdfLoading] = React.useState(false);

  useEffect(() => {
    if (isOpen) pdfPreview(activeContract.path);
  }, [isOpen]);

  const toggleSignature = () => setShowSignature((s) => !s);

  const pdfPreview = async (path) => {
    setPdfLoading(true);
    try {
      if (!path.includes('DSPOL')) {
        const link = await generateDAcontracts(
          daContractJson?.breakWaiver,
          path,
          selectedDsp,
          {
            ...daApplyFormData,
            drugProcedure: daContractJson?.drugProcedure,
            prescription: daContractJson?.prescription,
            notificationPolicy: daContractJson?.notificationPolicy,
          },
          signature,
          true,
        );
        setPdfLink(link);
      } else {
        const link = await generateOfferLetter(
          { ...selectedDsp, logo: selectedDsp?.logoPath || user?.userInfo?.dspLogo },
          { ...daApplyFormData, positionType: daApplyFormData.roleId },
          signature,
          true,
        );
        setPdfLink(link);
      }
    } catch {
      // handle error
    } finally {
      setPdfLoading(false);
    }
  };

  const onDoneSign = async () => {
    if (!signature) {
      showSnackbar('Please sign the contract', 'error');
      return;
    }
    toggleSignature();
    await pdfPreview(activeContract.path);
  };

  const onToggleSign = async () => {
    toggleSignature();
    await pdfPreview(activeContract.path);
  };

  const setField = (field) => (val) => setDAContractJSON((prev) => ({ ...prev, [field]: val }));

  const tabs = daContracts.map((item) => item.name);

  const drugProcedureOptions = [
    {
      id: '1',
      label:
        'Company will perform drug and alcohol testing on Covered Persons in a manner consistent with applicable law',
    },
    {
      id: '2',
      label:
        'Company may test for the presence of some or all of the substances defined above as Illegal Drugs and/or alcohol',
    },
    {
      id: '3',
      label:
        'Covered Persons have the right to refuse to undergo drug or alcohol testing. However, refusal to undergo drug or alcohol testing as set forth in this Policy, will result in discipline up to and including termination of employment. Applicants for employment who refuse to undergo drug and alcohol testing as set forth in this policy, will be ineligible for hire',
    },
  ];

  const prescriptionOptions = [
    {
      id: 'yes',
      label:
        'Yes — The Covered Person may be subject to further assessment if prescription drug use poses a direct threat or adversely affects job performance.',
    },
    {
      id: 'no',
      label:
        'No — The Covered Person will not be subject to further assessment based solely on valid prescription drug use.',
    },
  ];

  const notificationPolicyOptions = [
    { id: 'a', label: '(a) Statements in all recruiting ads' },
    { id: 'b', label: '(b) Notices posted at all hiring locations' },
    { id: 'c', label: '(c) Notices in all online career pages' },
    { id: 'd', label: '(d) Distributing this Policy directly to Covered Persons' },
    {
      id: 'e',
      label:
        '(e) Making copies of this Policy available for inspection during regular business hours',
    },
  ];

  const breakWaiverOptions = [
    {
      id: '3',
      label:
        'Yes — Effective the date of this waiver, I voluntarily waive my meal breaks as permitted by law in my state, until I revoke or withdraw this waiver.',
    },
    {
      id: '4',
      label:
        'Revoke — I wish to revoke my previous meal break waiver election, effective the date this waiver is executed.',
    },
  ];

  return (
    <>
      {/* ── Main contract viewer modal ── */}
      <Modal isOpen={isOpen} toggle={toggle} fullscreen>
        <ModalHeader toggle={toggle} className="border-bottom-custom" style={{ flexShrink: 0 }}>
          {activeContract.name}
        </ModalHeader>

        {/*
          ModalBody must be a flex column so PDFViewer can fill remaining height.
          overflow:hidden on ModalBody prevents double scrollbars — PDFViewer
          owns its own internal scroll container.
        */}
        <ModalBody
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Tabs — fixed height, does not scroll */}
          <div style={{ flexShrink: 0 }}>
            <FleetDashboardTabs
              fleetTabs={tabs}
              activeTab={activeTab}
              disable={pdfLoading}
              handleActiveTab={(index) => {
                setActiveContract(daContracts[index]);
                setActiveTab(index);
                pdfPreview(daContracts[index].path);
              }}
            />
          </div>

          {/* PDF area — takes ALL remaining vertical space */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {pdfLoading && <Spinner title={`Generating ${activeContract.name}`} />}
            {!pdfLoading && pdfLink && <PDFViewer pdfLink={pdfLink} />}
          </div>
        </ModalBody>

        <ModalFooter style={{ flexShrink: 0 }}>
          <Button title="Close" type="button" onClick={toggle} />
          {!disable && (
            <Button
              title={signature ? 'Change Signature' : 'Sign Contract'}
              type="button"
              onClick={toggleSignature}
            />
          )}
        </ModalFooter>
      </Modal>

      {/* ── Signature / form modal ── */}
      <Modal centered isOpen={showSignature} toggle={toggleSignature} size="xl">
        <ModalHeader toggle={onToggleSign}>
          <div>
            <p className="dac-form-header__title">Contract Acknowledgement</p>
            <p className="dac-form-header__sub">
              Review each section and confirm your selections before signing.
            </p>
          </div>
        </ModalHeader>

        <ModalBody className="dac-modal-body">
          <div className="dac-form-body">
            <PolicyRadioGroup
              badge="Notification"
              title="Policy Notification Method"
              policyText="Company will notify Covered Persons of this Policy by the selected method(s) below, in accordance with applicable law."
              options={notificationPolicyOptions}
              value={daContractJson?.notificationPolicy ?? 'a'}
              onChange={setField('notificationPolicy')}
            />
            <PolicyRadioGroup
              badge="Drug Testing"
              title="Type of Drug Testing Employed"
              policyText=""
              options={drugProcedureOptions}
              value={daContractJson?.drugProcedure ?? '1'}
              onChange={setField('drugProcedure')}
            />
            <PolicyRadioGroup
              badge="Prescription Use"
              title="Prescription Drug Further Assessment"
              policyText="A Covered Person who is using or tests positive for a prescription drug for which they have a valid prescription, but which drug use may pose a direct threat to the employee or others in the workplace (or which otherwise adversely affects the employee's job performance), may be subject to further assessment."
              options={prescriptionOptions}
              value={daContractJson?.prescription ?? 'yes'}
              onChange={setField('prescription')}
            />
            <PolicyRadioGroup
              badge="Meal Break"
              title="Meal Break Waiver Preference"
              options={breakWaiverOptions}
              value={daContractJson?.breakWaiver?.toString() ?? '3'}
              onChange={setField('breakWaiver')}
            />
          </div>

          <div className="dac-signature-wrapper">
            <SignatureBoard
              label="Add Signature"
              signature={signature}
              setSignature={(svg) => setSignature(svg)}
              width={400}
            />
            <p className="dac-signature-hint">
              By signing, you confirm you have read and understood all agreement documents. Your
              signature applies to all sections above.
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="dac-footer">
            <button onClick={onToggleSign} type="button" className="dac-btn-outline">
              Cancel
            </button>
            <button onClick={onDoneSign} type="button" className="dac-btn-primary">
              Confirm &amp; Sign
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default DAContractModal;
